import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { dispatch, store } from 'store';

import { CompanyModel } from 'types/company/company';
import { fetcher } from 'utils/axios';
import {
  extractAdminTokenIdFromCompany,
  syncFireberryMetadataTokenCache,
} from 'utils/fireberryMetadataToken';

const initialState: CompanyModel | null = null;

const companySlice = createSlice({
  name: 'company',
  initialState: initialState as CompanyModel | null,
  reducers: {
    setCompanyData(state, action: PayloadAction<CompanyModel>) {
      return action.payload;
    },
    updateCompanyData: (state, action: PayloadAction<Partial<CompanyModel>>) => {
      if (state) {
        Object.assign(state, action.payload);
      }
    },
    updateCompanyDataWithParams(state, action: PayloadAction<Partial<CompanyModel>>) {
      if (state) {
        const params = action.payload;
        if (params) {
          Object.assign(state, params);
        }
      }
    },
    resetCompanyData(state) {
      return initialState;
    },
  },
});

export default companySlice.reducer;
export const { setCompanyData, updateCompanyData, updateCompanyDataWithParams, resetCompanyData } =
  companySlice.actions;

function normalizeCompanyDetails(data: unknown): CompanyModel | null {
  if (!data || typeof data !== 'object') return null;
  const adminTokenId = extractAdminTokenIdFromCompany(data as CompanyModel);
  return { ...(data as CompanyModel), AdminTokenId: adminTokenId };
}

export async function getCompanyData() {
  try {
    const data = await fetcher('get', '/api/services/GetCompanyDetails', 'history');
    const normalized = normalizeCompanyDetails(data);
    if (normalized) {
      dispatch(setCompanyData(normalized));
      if (normalized.AdminTokenId) {
        syncFireberryMetadataTokenCache(normalized.AdminTokenId);
      }
    }
  } catch (error) {
    console.warn('getCompanyData err: ', error);
  }
}
export async function fetchUpdateCompanyData(
  params: Partial<CompanyModel>,
  sendNotification: boolean,
): Promise<boolean> {
  try {
    let currentCompanyData = store.getState().company;
    if (!currentCompanyData) {
      await getCompanyData();
      currentCompanyData = store.getState().company;
    }
    if (!currentCompanyData) {
      if (sendNotification) {
        enqueueSnackbar('נתוני החברה עדיין לא נטענו — רענן את הדף ונסה שוב', {
          variant: 'warning',
        });
      }
      return false;
    }

    const updatedCompanyData = { ...currentCompanyData, ...params };

    const data = await fetcher(
      'post',
      ['/api/services/SetCompanyConfig', { data: updatedCompanyData }],
      'history',
    );
    if (data) {
      dispatch(updateCompanyData(params));
      if (params.AdminTokenId !== undefined) {
        syncFireberryMetadataTokenCache(String(params.AdminTokenId ?? ''));
      }
    }
    if (sendNotification) {
      enqueueSnackbar(data ? 'הפעולה בוצעה בהצלחה' : 'חלה שגיאה בעיבוד הנתונים', {
        variant: data ? 'success' : 'error',
      });
    }
    return !!data;
  } catch (error) {
    console.warn('fetchUpdateCompanyData err: ', error);
    if (sendNotification)
      enqueueSnackbar('חלה שגיאה בעיבוד הנתונים', {
        variant: 'error',
      });
    return false;
  }
}
